import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@monbits/ui";
import type * as React from "react";
import { useForm } from "react-hook-form";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { BLOCKCHAIN, type MerchantAddressParams } from "src/types";
import * as z from "zod";

interface Props {
	onFinish: (values: MerchantAddressParams, reset: () => void) => Promise<void>;
	isFormSubmitting: boolean;
}

const formSchema = z.object({
	blockchain: z.string().min(1, "Field is required"),
	name: z.string().min(1, "Field is required"),
	address: z.string().min(1, "Field is required"),
});

type FormValues = z.infer<typeof formSchema>;

const AddressCreateForm: React.FC<Props> = (props: Props) => {
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			blockchain: "",
			name: "",
			address: "",
		},
	});

	const onSubmit = async (values: FormValues) => {
		try {
			await props.onFinish(values as MerchantAddressParams, form.reset);
		} catch (error: unknown) {
			const responseErrors = (
				error as {
					response?: {
						data?: { errors?: Array<{ field: string; message: string }> };
					};
				}
			).response?.data?.errors;

			if (responseErrors) {
				for (const err of responseErrors) {
					if (
						err.field === "address" ||
						err.field === "blockchain" ||
						err.field === "name"
					) {
						form.setError(err.field as keyof FormValues, {
							type: "manual",
							message: err.message,
						});
					}
				}
			} else {
				const responseMessage = (
					error as { response?: { data?: { message?: string } } }
				).response?.data?.message;
				form.setError("address", {
					type: "manual",
					message:
						responseMessage || "Failed to create address. Please try again.",
				});
			}
		}
	};

	return (
		<Form {...form}>
			<form
				id="address-create-form"
				onSubmit={form.handleSubmit(onSubmit)}
				className="space-y-8"
			>
				<FormField
					control={form.control}
					name="blockchain"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Blockchain</FormLabel>
							<Select onValueChange={field.onChange} value={field.value}>
								<FormControl>
									<SelectTrigger className="w-full">
										{field.value ? (
											<CryptoIcon ticker={field.value} showLabel />
										) : (
											<SelectValue placeholder="Select blockchain" />
										)}
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{BLOCKCHAIN.map((item) => (
										<SelectItem key={item} value={item}>
											<CryptoIcon ticker={item} showLabel />
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="My TrustWallet" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="address"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Blockchain Address</FormLabel>
							<FormControl>
								<Input placeholder="0x9cEF7DE49...." {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{/* Hidden submit button for form validation */}
				<Button type="submit" className="sr-only">
					Submit
				</Button>
			</form>
		</Form>
	);
};

export default AddressCreateForm;
